import {
  Box,
  CircularProgress,
  Paper,
  Button,
  Grid,
  useMediaQuery,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import {
  selectAbsertCodesList,
  selectEmployeeData,
  selectPtoBalancesEmployeeCSVData,
  selectPtoBalancesEmployeeData,
} from '../../../store/ReportsDetailsSlice/selectors';
import { useEffect, useMemo } from 'react';
import {
  getAbsertCodesList,
  getEmployeeList,
  getPtoBalancesEmployee,
  getPtoBalancesEmployeeCsv,
  setPtoBalancesEmployeeCsv,
} from '../../../store/ReportsDetailsSlice/actions';
import { useRowsAccrualBalance } from './hooks/useRows';
import ReportsHeader from '../../../components/Reports/ReportsHeader';
import { useColumns } from './hooks/useColumns';
import DataNotFound from '../../../components/Reports/DataNotFound';
import TableItem from './TableItem';
import { FilterPanel } from '../../../components/Reports/FilterPanel';
import SelectRebuild from '../../../components/Inputs/SelectRebuild';
import { GROUPBY_ACCRUAL } from '../../../constants/Reports';
import { useForm } from 'react-hook-form';
import { ExportCsvButton } from '../../../components/Buttons/ExportCsvButton';
import { saveCsvToFile } from '../../../utils/saveCsvToFile';
import './accrualBalance.css';
import { PrintButton } from '../../../components/Buttons/PrintButton';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter';
import moment from 'moment';

const createParams = (param) =>
  param && param.length > 0 ? param.map((par) => par.id) : 'ALL';

const AccrualBalance = () => {
  const dispatch = useDispatch();
  const { ptoBalancesEmployee, loadingPtoBalancesEmployee } = useSelector(
    selectPtoBalancesEmployeeData
  );

  const { ptoBalancesEmployeeCsv, loadingPtoBalancesEmployeeCsv } = useSelector(
    selectPtoBalancesEmployeeCSVData
  );

  const isTabletVerticalWidth = useMediaQuery('(max-width:1024px)');

  const { employeeList, loadingEmployeeList } = useSelector(selectEmployeeData);
  const { absertCodesList, loadingAbsertCodesList } = useSelector(
    selectAbsertCodesList
  );

  const { fromDate, toDate, render } = useDateRangeFilter(true);

  const { register, setValue, watch } = useForm({
    defaultValues: {
      emp_code: [],
      groupby: { id: 'Emp', name: 'Employee' },
      accrualType: [],
      includeZero: false,
    },
  });
  const { emp_code: employees, groupby, accrualType, includeZero } = watch();

  const employeeParam = useMemo(() => createParams(employees), [employees]);
  const accrualTypeParam = useMemo(
    () => createParams(accrualType),
    [accrualType]
  );

  const param = useMemo(() => {
    return {
      sel_order: groupby.id,
      sel_emp:
        employeeParam !== 'ALL' && employeeParam.length < employeeList.length
          ? employeeParam
          : null,
      arr_pto_type:
        accrualTypeParam !== 'ALL' &&
        accrualTypeParam.length < absertCodesList.length
          ? accrualTypeParam
          : null,
      include_zero: includeZero ? 1 : 0,
      start_dt: moment(fromDate).format('YYYY-MM-DD') + ' 00:00:00',
      end_dt: moment(toDate).format('YYYY-MM-DD') + ' 23:59:59',
    };
  }, [
    groupby,
    employeeParam,
    accrualTypeParam,
    absertCodesList,
    employeeList,
    includeZero,
    fromDate,
    toDate,
  ]);

  useEffect(() => {
    dispatch(getPtoBalancesEmployee(param));
    dispatch(getEmployeeList());
    dispatch(getAbsertCodesList());
  }, []);

  useEffect(() => {
    if (ptoBalancesEmployeeCsv?.data) {
      saveCsvToFile(
        ptoBalancesEmployeeCsv.data,
        ptoBalancesEmployeeCsv.filename
      );
      dispatch(setPtoBalancesEmployeeCsv({ data: null, filename: '' }));
    }
  }, [ptoBalancesEmployeeCsv, dispatch]);

  const handleFindClick = () => dispatch(getPtoBalancesEmployee(param));

  const handleDownloadReport = () => {
    const query = { ...param, csv: 1 };
    dispatch(getPtoBalancesEmployeeCsv(query));
  };

  const rows = useRowsAccrualBalance(ptoBalancesEmployee);

  const columns = useColumns(ptoBalancesEmployee.sel_order);

  const handlePrint = () => {
    window.print();
  };

  const rawFilterConfig = [
    {
      key: 'group_by',
      label: 'Group By',
      show: GROUPBY_ACCRUAL.length > 0,
      renderComponent: () => (
        <SelectRebuild
          options={GROUPBY_ACCRUAL.map((item) => ({
            id: item.value,
            name: item.title,
          }))}
          name="groupby"
          label="Group By"
          multiple={false}
          loading={false}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'emp_code',
      label: 'Employee',
      show: true,
      renderComponent: () => (
        <SelectRebuild
          options={employeeList}
          name="emp_code"
          label="Employee"
          multiple
          allOption={{ id: 'ALL', name: 'All' }}
          loading={loadingEmployeeList}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },

    {
      key: 'arr_pto_type',
      label: 'Accrual type',
      show: true,
      renderComponent: () => (
        <SelectRebuild
          options={absertCodesList}
          name="accrualType"
          label="Employee"
          multiple
          allOption={{ id: 'ALL', name: 'All' }}
          loading={loadingAbsertCodesList}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },

    {
      key: 'include-zero-balances',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            gap: 2,
            justifyContent: 'start',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox checked={includeZero} {...register('includeZero')} />
            }
            label="Include zero balances"
          />

          <Button
            variant="contained"
            onClick={handleFindClick}
            sx={{ whiteSpace: 'nowrap', height: '32px' }}
          >
            Find
          </Button>
        </Grid>
      ),
    },
    {
      key: 'export',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            gap: 2,
            justifyContent: isTabletVerticalWidth ? 'start' : 'end',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <ExportCsvButton
            onClick={handleDownloadReport}
            disabled={
              loadingPtoBalancesEmployeeCsv || loadingPtoBalancesEmployee
            }
            text={'Export CSV'}
          />
          <PrintButton
            onClick={handlePrint}
            disabled={loadingPtoBalancesEmployee}
            text={'Print'}
          />
        </Grid>
      ),
    },
  ];

  const filterConfig = rawFilterConfig.filter((f) => f.show !== false);

  return (
    <Paper className="p-4" sx={{ marginTop: '1rem' }}>
      <ReportsHeader title="Accrual Balance Details" />
      <Box className="mt-4 mb-4" sx={{ width: '100%' }}>
        <Grid container sx={{ width: '100%', mb: '24px' }}>
          <FilterPanel
            filters={filterConfig}
            columns={isTabletVerticalWidth ? 1 : 5}
          />
        </Grid>

        <Grid container sx={{ width: '100%' }}>
          <Grid
            item
            sx={{
              display: 'flex',
              gap: 2,
              width: '100%',
              alignItems: 'center',
            }}
          >
            {render}
          </Grid>
        </Grid>
      </Box>

      {loadingPtoBalancesEmployee ? (
        <Box
          sx={{ minHeight: '30px' }}
          className="w-10 h-10 d-flex justify-content-center align-items-center"
        >
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            gap: 4,
            flexDirection: 'column',
          }}
        >
          {rows.length === 0 ? (
            <DataNotFound />
          ) : (
            <Box id="print-area">
              {rows.map((item) => (
                <TableItem
                  key={item.name}
                  item={item}
                  columns={columns}
                  absertCodesList={absertCodesList}
                  selOrder={ptoBalancesEmployee.sel_order}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default AccrualBalance;
